import React from 'react';
import {
  Component,
  getUnixDate,
  HBox,
  Position,
  PuzzleButton,
} from "puzzle"
import {
  GridCellProps,
  GridColumn as Column, GridNoRecords
} from "@progress/kendo-react-grid";
import _ from "lodash";
import { GridColumnProps } from "@progress/kendo-react-grid/dist/npm/interfaces/GridColumnProps";
import KendoGridAbstract from "../../../util/KendoGridAbstract/KendoGridAbstract";
import {
  defaultCellRender,
  getTableTdAccessibilityProps,
  isDateExpired,
  isSubmissionDueSoon,
  SUBMISSION_STATUSES_MAP_SERVER
} from "../../../util/Util";
import { GRID_COLUMN_ITEM } from "../../../Drawer/Tabs/Files/GradingAndScoring/Types";
import { SUBMISSION } from "../../../Abstract/Grid/Types";

type REQUEST_PARAMS = {
  applyDueDateAscendingSort: true
  ascending: true
  folder: "DEFAULT_SUBMISSION"
  fromDueDate: number
  size: 10 | 50
  toDueDate: number
}

type PROPS = {
  activeTab: boolean
  dueSoonRange: number
};

type STATE = {
  data: SUBMISSION[]
};

class DueSoonOverdueGrid extends Component<PROPS, STATE> {
  gridConfig: { dataIndex: string, width?: number } [] = [{
    dataIndex: 'submissionId',
    width: 150
  }, {
    dataIndex: 'submissionName'
  }, {
    dataIndex: 'dueDate',
    width: 180
  }];

  state: STATE = {
    data: []
  };

  /**
   * Prepare request params for dueSoon/overDue grid
   */
  createRequestData = () => {
    const { activeTab } = this.props;
    const currentDate = new Date();
    const slidingDays = new Date();
    const days = activeTab ? -7 : 7;
    slidingDays.setDate(currentDate.getDate() + days);

    const requestParams: REQUEST_PARAMS = {
      applyDueDateAscendingSort: true,
      ascending: true,
      folder: "DEFAULT_SUBMISSION",
      fromDueDate: activeTab ? getUnixDate(slidingDays) : getUnixDate(currentDate),
      size: 10,
      toDueDate: activeTab ? getUnixDate(currentDate) : getUnixDate(slidingDays),
      ...(!activeTab && { submissionStatusValues: SUBMISSION_STATUSES_MAP_SERVER.ACTIVE }) // apply filter for active subs
    };

    return requestParams;
  };

  /**
   * Adds proper indicator color (red,orange, none)
   */
  createIndicatorColor = (dataItem: { dueDate: { date: number } }) => {
    let className = '';
    if (isDateExpired(dataItem.dueDate.date)) {
      className = 'tp-status-expired';
    } else if (isSubmissionDueSoon(dataItem.dueDate.date, this.props.dueSoonRange)) {
      className = 'tp-status-warning';
    }

    return className;
  }

  /** Grid cell render
   * @param cellProps
   */
  cellRender = (cellProps: GridCellProps): JSX.Element => {
    const {
      dataItem,
      field
    } = cellProps;

    const tdProps: any = getTableTdAccessibilityProps(cellProps);
    let editedProps = _.cloneDeep(cellProps)
    let child;

    switch (field) {
      case 'submissionId':
        editedProps.className = `pd-widget-submission-id`;
        child = this.submissionIdRender(dataItem);
        break;
      case 'dueDate':
        child = this.dueDateRenderer(dataItem[field].date);
        break;
      default:
        child = defaultCellRender(this.translate, () => {
        }, cellProps);
        break;
    }

    return <td {...tdProps} className={`${editedProps.className}`}>{child}</td>;
  };

  /**
   * Due date cell render
   */
  dueDateRenderer = (date: number): JSX.Element => {
    let className = '';
    if (isDateExpired(date)) {
      className = 'tp-color-error';
    }

    return <span className={`${className} tp-pointer-events-none`}>
      {this.translateByNamespace('date.dateWithTime', { date: new Date(date) })}
    </span>
  };

  /**
   * Method that renders files grid columns
   * @param column
   */
  renderColumn = (column: GRID_COLUMN_ITEM): JSX.Element => {
    let dataIndex: string = column.dataIndex;
    let columnProps: GridColumnProps = {
      field: dataIndex,
      title: this.translate(`columnHeaders.${dataIndex}`),
      cell: this.cellRender,
      ...(column.width && { width: column.width })
    };

    return <Column
      key={dataIndex}
      {...columnProps}
    />;
  };

  /***
   * Row click handler, leads to submission list prefiltered by submissionId
   */
  rowClickHandler = ({ dataItem }: { dataItem: SUBMISSION }) => {
    this.dispatch('selectionChangeWithToolbarUpdate', Position.end, {
      currentModuleName: 'SubmissionsList',
      forceOpenDrawer: true,
      selectedSubmissions: [{ ...dataItem }]
    });
  };

  /**
   * Sets grid data
   */
  setData = ({ items: data }: { items: SUBMISSION[] }) => {
    this.setState({ data });
  };

  /**
   * Submission Id Render
   */
  submissionIdRender = (dataItem: { submissionId: number, dueDate: { date: number } }) => {
    return <HBox className={'tp-pointer-events-none'}>
      <span className={`pd-status-color-column-indicator ${this.createIndicatorColor(dataItem)}`} />
      <span className={'pd-id-cell'}>
       {dataItem.submissionId}
      </span>
    </HBox>
  };

  /**
   * Performs search and redirect to submission list on click
   */
  showAllButtonHandler = () => {
    let filterSearchData = _.omit(this.createRequestData(), ['applyDueDateAscendingSort', 'ascending']);
    filterSearchData.size = 50;
    const operationData = {
      decodeFilter: true,
      filter: { searchData: JSON.stringify(filterSearchData) },
      path: '/view/submissionList'
    };

    this.dispatch('dueSoonOverdueRowClick', '', {
      ...operationData
    });
  }

  renderBody(): JSX.Element {
    const { data } = this.state;
    return (
      <>
        <KendoGridAbstract
          captionText={this.translate("gridCaption")}
          className={'tp-no-border'}
          data={data}
          rowHeight={40}
          onRowClick={this.rowClickHandler}
        >
          <GridNoRecords>
            {!data.length ?
              (<div className='k-vbox k-justify-content-center k-align-items-center'>
                <div className={`tp-grid-empty-state-img subs-grid`} />
                <div
                  className='tp-grid-empty-state-text tp-color-light-LM tp-text-semibold k-text-center tp-font-size-xl'>
                  {this.props.activeTab ? this.translate('emptyGridLabelOverdue') : this.translate('emptyGridLabelDueSoon')}
                </div>
              </div>) : (<div></div>)
            }
          </GridNoRecords>
          {this.gridConfig.map(this.renderColumn)}
        </KendoGridAbstract>
        <PuzzleButton
          className={'k-align-self-end'}
          themeColor={'primary'}
          fillMode={'outline'}
          onClick={this.showAllButtonHandler}
        >
          {this.translate('showAll')}
        </PuzzleButton>
      </>
    );
  }

}

export default DueSoonOverdueGrid;
